import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api.service';
import { shouldSkipApiCalls } from '../utils/routeUtils';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    console.log('🔍 AuthContext: Starting authentication check...');
    try {
      // Try to get current user - the backend will check httpOnly cookies
      console.log('🌐 AuthContext: Calling getCurrentUser API...');
      const user = await authApi.getCurrentUser();
      console.log('✅ AuthContext: Authentication successful:', {
        userId: user?.admin?.id,
        email: user?.admin?.email
      });
      setIsAuthenticated(true);
      setIsLoading(false);
    } catch (error) {
      console.log('❌ AuthContext: Authentication failed:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        status: (error as any)?.response?.status,
        statusText: (error as any)?.response?.statusText
      });
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
      // logout already redirects to '/', no need to call checkAuth
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to login
      window.location.href = '/';
    }
  };

  useEffect(() => {
    console.log('🚀 AuthContext: useEffect triggered, current path:', window.location.pathname);

    // Skip auth check on public pages
    if (shouldSkipApiCalls()) {
      console.log('🚫 AuthContext: Skipping auth check for public route:', window.location.pathname);
      setIsLoading(false);
      setIsAuthenticated(false);
      return;
    }

    console.log('🔐 AuthContext: Starting auth check for protected route:', window.location.pathname);
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};